import { useGetFile } from "@/queries/files/file.api";
import type { FileType } from "@/types/common/files";
import { cn } from "@/utils/theme";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { memo, type ComponentProps } from "react";

const isExpiredFromUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const expires = urlObj.searchParams.get("Expires");
    if (expires) {
      const expiration = new Date(Number.parseInt(expires, 10) * 1000);
      // Convert Unix timestamp to Date
      return expiration && expiration < new Date();
    } else {
      // if no expiration param, consider expired and required the API call
      // just in case, u know
      return true;
    }
  } catch (error) {
    console.error("Error extracting expiration from URL:", error);
  }
  return false;
};
export interface FileLinkProps extends ComponentProps<typeof Badge> {
  file: FileType | string;
  defaultValue?: string;
}

const FileLinkComponent = ({
  file,
  defaultValue,
  className,
  ...props
}: FileLinkProps) => {
  // Parse file if it's a string
  const parsedFile: FileType | null =
    typeof file === "string"
      ? (() => {
          try {
            return JSON.parse(file);
          } catch (error) {
            console.error("Error parsing file data:", error);
            return null;
          }
        })()
      : file;

  const isExpired = parsedFile ? isExpiredFromUrl(parsedFile.url) : false;
  const { data: fileData } = useGetFile(parsedFile?.id ?? "", isExpired);
  const currentUrl = fileData?.data?.url || parsedFile?.url;

  if (!parsedFile) {
    return <>{defaultValue || ""}</>;
  }

  const handleClick = () => {
    if (!currentUrl) return;
    window.open(currentUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Badge
      variant="neutral"
      size="sm"
      className={cn("cursor-pointer", className)}
      role="button"
      {...props}
      onClick={handleClick}
    >
      <p className="truncate">{parsedFile?.file_name || parsedFile?.name}</p>
      <Icon type="fas" name="file" size="sm" />
    </Badge>
  );
};

export const FileLink = memo(FileLinkComponent);
